import React, { useState } from 'react';
import { Table, Button, Badge } from 'react-bootstrap';
import { IconEye } from '@tabler/icons-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { LogData, LogResponse } from '../../../services/log/response/LogResponse';
import { format, parseISO } from 'date-fns';
import LogDetailDrawer from './LogDetailDrawer';

// Fungsi untuk mengambil nilai dari objek dengan path dot notation
const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

// Renders old_value/new_value (see AuditLogger) as a compact "field: old → new"
// list — falls back to '-' when a row predates these columns or has neither.
// Kept short on purpose (see MAX_DIFF_PREVIEW_LENGTH): the full, untruncated
// version is always one click away via LogDetailDrawer, so this stays a scan-
// friendly preview rather than a wall of text competing with every other row.
const MAX_DIFF_PREVIEW_LENGTH = 70;

const formatDiff = (row: LogData): string => {
    const oldValue = row.old_value as Record<string, unknown> | null | undefined;
    const newValue = row.new_value as Record<string, unknown> | null | undefined;

    if (!oldValue && !newValue) return '-';

    const keys = Array.from(new Set([...Object.keys(oldValue ?? {}), ...Object.keys(newValue ?? {})]));
    if (keys.length === 0) return '-';

    return keys
        .map((key) => {
            const before = oldValue?.[key];
            const after = newValue?.[key];
            if (before === undefined) return `${key}: ${String(after)}`;
            // Deletion rows carry only old_value (a snapshot of what was removed) —
            // show what it was rather than a bare "removed".
            if (!newValue) return `${key}: ${String(before)}`;
            if (after === undefined) return `${key} removed`;
            return `${key}: ${String(before)} → ${String(after)}`;
        })
        .join('; ');
};

const formatDiffPreview = (row: LogData): string => {
    const full = formatDiff(row);
    return full.length > MAX_DIFF_PREVIEW_LENGTH ? full.slice(0, MAX_DIFF_PREVIEW_LENGTH) + '…' : full;
};

// A handful of very old rows in `logs` predate this action's naming — some
// were written with an empty string. Without this, those rows render as a
// blank, textless badge (looks broken) instead of something a user can read.
const formatActionLabel = (action: string | null | undefined): string => {
    const trimmed = (action ?? '').trim();
    if (!trimmed) return 'Unknown action';
    return trimmed
        .replace(/_/g, ' ')
        .replace(/^./, (c) => c.toUpperCase());
};

const actionBadgeVariant = (action: string | null | undefined): string => {
    const actionText = (action ?? '').trim().toLowerCase();
    if (!actionText) return 'light';
    if (actionText.startsWith('create')) return 'success';
    if (actionText.startsWith('update')) return 'primary';
    if (actionText.startsWith('delete') || actionText.startsWith('remove')) return 'danger';
    if (actionText.includes('failed')) return 'danger';
    if (actionText.startsWith('channex')) return 'info';
    return 'secondary';
};

// Helper function to format date
const formatDateLocal = (dateString: string) => {
    if (!dateString) return '-';
    try {
        // Parse the UTC ISO string
        const parsedDate = parseISO(dateString);
        // Format to a readable string (e.g., "14 Mar 2026, 20:38:32")
        // Date-fns format will automatically convert it to the browser's local timezone
        return format(parsedDate, 'dd MMM yyyy, HH:mm:ss');
    } catch (error) {
        console.error("Error parsing date:", error);
        return dateString;
    }
};

interface Props {
    data: LogResponse;
    columns: {
        label: string;
        key: string;
    }[];
    /** Lets the caller filter its list by a log's correlation_id (see the detail drawer). */
    onViewRelated?: (correlationId: string) => void;
}

const LogTable: React.FC<Props> = ({ data, columns, onViewRelated }) => {
    const [selectedLog, setSelectedLog] = useState<LogData | null>(null);

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Log PMS", 14, 10);

        const tableData = data.data.map(row => columns.map(col => {
            // Handle date formatting for export
             if (col.key === 'created_at') {
                 return formatDateLocal(getNestedValue(row, col.key));
             }
             if (col.key === 'diff') {
                 return formatDiff(row);
             }
             return getNestedValue(row, col.key);
        }));

        autoTable(doc, {
            head: [columns.map(col => col.label)],
            body: tableData,
        });

        doc.save("Log_pms.pdf");
    };

    // Export to Excel
    const exportToExcel = () => {
        const wsData = [
            columns.map(col => col.label),
            ...data.data.map(row => columns.map(col => {
                // Handle date formatting for export
                if (col.key === 'created_at') {
                     return formatDateLocal(getNestedValue(row, col.key));
                }
                if (col.key === 'diff') {
                    return formatDiff(row);
                }
                return getNestedValue(row, col.key);
            })),
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "Log_PMS.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columns.map((col, index) => (
                                <th key={index}>{col.label}</th>
                            ))}
                            <th className="text-end">Detail</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.data.length > 0 ? (
                            data.data.map((row: LogData, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((col, colIndex) => {
                                        const value = getNestedValue(row, col.key);

                                        // Penanganan khusus untuk key "action" — badge warna per jenis aksi,
                                        // jauh lebih cepat di-scan daripada teks polos di antara kolom lain.
                                        if (col.key === "action") {
                                            const variant = actionBadgeVariant(value as string | null | undefined);
                                            return (
                                                <td key={colIndex}>
                                                    {/* Tabler's theme sets a muted gray as the .badge default text
                                                        color, and bg-* utility classes only set the background —
                                                        so the color must be forced explicitly or it reads as
                                                        low-contrast gray-on-color for every variant. */}
                                                    <Badge bg={variant} text={variant === 'light' ? 'dark' : 'white'} className="text-capitalize">
                                                        {formatActionLabel(value as string | null | undefined)}
                                                    </Badge>
                                                </td>
                                            );
                                        }

                                        // Penanganan khusus untuk tanggal (created_at)
                                        if (col.key === "created_at") {
                                             return (
                                                <td key={colIndex}>
                                                    {formatDateLocal(value as string)}
                                                </td>
                                            );
                                        }

                                        // "Changes" column — short preview of old_value -> new_value (see
                                        // AuditLogger); the untruncated version is one click away below.
                                        if (col.key === "diff") {
                                            return (
                                                <td key={colIndex} className="small text-muted">
                                                    {formatDiffPreview(row)}
                                                </td>
                                            );
                                        }

                                        return (
                                            <td key={colIndex}>
                                                {value}
                                            </td>
                                        );
                                    })}
                                    <td className="text-end">
                                        <Button
                                            variant="light"
                                            size="sm"
                                            title="View full detail"
                                            onClick={() => setSelectedLog(row)}
                                        >
                                            <IconEye size={16} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length + 1} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <LogDetailDrawer
                show={selectedLog !== null}
                onClose={() => setSelectedLog(null)}
                log={selectedLog}
                onViewRelated={onViewRelated ? (correlationId) => {
                    setSelectedLog(null);
                    onViewRelated(correlationId);
                } : undefined}
            />
        </div>
    );
};

export default LogTable;
